import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  aplicarReajusteProdutos,
  getAllBrands,
  getAllCategories,
  getAllProducts,
  getAllSuppliers,
  getAllTributaryGroups,
  getEstabelecimentos,
  getSubCategoriesByCategory,
  getTabelasPreco,
  previewReajusteProdutos,
} from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import { normalizeSearchTerm } from '../../utils/productSearch';
import PageHeader from '../common/PageHeader';
import ResponsiveTable from '../common/ResponsiveTable';
import BuscaProdutoCampo from '../common/BuscaProdutoCampo';

const PASSOS = ['Produtos', 'Alterações', 'Conferência'];
const UNIDADES = ['UN', 'KG', 'LT', 'MT', 'PC', 'CX'];
const PAGE = 20;
const LOTE = 200;
const MAX_LISTA = 1000;

const listaDe = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const num = (valor) => {
  const n = Number(String(valor ?? '').trim().replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const nomeDe = (item) => item?.descricao || item?.nomeFantasia || item?.razaoSocial || item?.nome || '';

const cadastroInicial = {
  alterarCategoria: false,
  categoriaId: '',
  alterarSubCategoria: false,
  subCategoriaId: '',
  alterarMarca: false,
  marcaId: '',
  alterarFornecedor: false,
  fornecedorId: '',
  alterarUnidade: false,
  unidade: 'UN',
  alterarGrupoTributario: false,
  grupoTributarioId: '',
  alterarNcm: false,
  ncm: '',
  alterarCest: false,
  cest: '',
  alterarQtdAtacado: false,
  qtdAtacado: '',
  alterarValidade: false,
  validade: '',
  alterarComissao: false,
  percentualComissao: '',
  alterarAtivo: false,
  ativo: true,
  alterarBalanca: false,
  balanca: 0,
  alterarCombustivel: false,
  combustivel: false,
  alterarObrigaIdentificacaoCliente: false,
  obrigaIdentificacaoCliente: false,
};

const NOME_BALANCA = {
  0: 'Nenhuma',
  1: 'Checkout',
  2: 'Carga',
  3: 'Ambos',
};

export default function ReajusteProdutosPage() {
  const [passo, setPasso] = useState(0);
  const [loading, setLoading] = useState(true);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');
  const [aviso, setAviso] = useState('');

  const [filiais, setFiliais] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [gruposTrib, setGruposTrib] = useState([]);
  const [tabelas, setTabelas] = useState([]);
  const [subsFiltro, setSubsFiltro] = useState([]);
  const [subsCadastro, setSubsCadastro] = useState([]);

  const [termo, setTermo] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [subCategoriaId, setSubCategoriaId] = useState('');
  const [eanDe, setEanDe] = useState('');
  const [eanAte, setEanAte] = useState('');

  const [resultados, setResultados] = useState([]);
  const [totalBusca, setTotalBusca] = useState(0);
  const [paginaBusca, setPaginaBusca] = useState(1);
  const [marcados, setMarcados] = useState({});
  const [cesta, setCesta] = useState([]);
  const [paginaCesta, setPaginaCesta] = useState(1);
  const [removerIds, setRemoverIds] = useState({});

  const [filialId, setFilialId] = useState('');
  const [cadastro, setCadastro] = useState(cadastroInicial);
  const [tabelaId, setTabelaId] = useState('');
  const [tipos, setTipos] = useState({ varejo: true, atacado: false, promocional: false, custo: false });
  const [operacao, setOperacao] = useState('percentual');
  const [valorPreco, setValorPreco] = useState('');
  const [arredondamento, setArredondamento] = useState('nenhum');
  const [resultado, setResultado] = useState(null);

  const idsNaCesta = useMemo(() => new Set(cesta.map((p) => p.id)), [cesta]);

  const tabelasFilial = useMemo(
    () => tabelas.filter((t) => String(t.estabelecimentoId) === String(filialId)),
    [tabelas, filialId],
  );

  useEffect(() => {
    if (tabelasFilial.length === 0) {
      setTabelaId('');
      return;
    }
    if (!tabelasFilial.some((t) => String(t.id) === String(tabelaId))) {
      setTabelaId(String(tabelasFilial[0].id));
    }
  }, [tabelasFilial, tabelaId]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const [est, cats, br, forn, trib, tabs] = await Promise.all([
        getEstabelecimentos(),
        getAllCategories({ indice: 1, qtde: 500 }),
        getAllBrands({ indice: 1, qtde: 500 }),
        getAllSuppliers({ indice: 1, qtde: 500 }),
        getAllTributaryGroups({ indice: 1, qtde: 500 }),
        getTabelasPreco(),
      ]);
      const filiaisLista = listaDe(est.data);
      setFiliais(filiaisLista);
      setCategorias(listaDe(cats.data));
      setMarcas(listaDe(br.data));
      setFornecedores(listaDe(forn.data));
      setGruposTrib(listaDe(trib.data));
      setTabelas(listaDe(tabs.data));
      if (filiaisLista[0]) setFilialId(String(filiaisLista[0].id));
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível carregar os cadastros.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!categoriaId) {
      setSubsFiltro([]);
      setSubCategoriaId('');
      return;
    }
    let ativo = true;
    getSubCategoriesByCategory(categoriaId)
      .then(({ data }) => {
        if (ativo) setSubsFiltro(listaDe(data));
      })
      .catch(() => {
        if (ativo) setSubsFiltro([]);
      });
    return () => { ativo = false; };
  }, [categoriaId]);

  useEffect(() => {
    if (!cadastro.alterarCategoria || !cadastro.categoriaId) {
      setSubsCadastro([]);
      return;
    }
    let ativo = true;
    getSubCategoriesByCategory(cadastro.categoriaId)
      .then(({ data }) => {
        if (ativo) setSubsCadastro(listaDe(data));
      })
      .catch(() => {
        if (ativo) setSubsCadastro([]);
      });
    return () => { ativo = false; };
  }, [cadastro.alterarCategoria, cadastro.categoriaId]);

  const filtrosBusca = () => ({
    termo: normalizeSearchTerm(termo),
    categoriaId: categoriaId || '',
    subCategoriaId: subCategoriaId || '',
    eanDe: eanDe.trim(),
    eanAte: eanAte.trim(),
  });

  const temFiltro = () => {
    const f = filtrosBusca();
    return Boolean(f.termo || f.categoriaId || f.subCategoriaId || f.eanDe || f.eanAte);
  };

  const incorporar = (lista) => {
    setCesta((atual) => {
      const vistos = new Set(atual.map((p) => p.id));
      const novos = lista
        .filter((p) => p?.id && !vistos.has(p.id))
        .map((p) => ({
          id: p.id,
          codigo: p.codigo,
          descricao: p.descricao,
          ean: p.ean,
          categoria: p.categoriaNome || p.categoria || '',
        }));
      return novos.length ? [...atual, ...novos] : atual;
    });
  };

  const buscar = async (pagina = 1) => {
    if (!temFiltro()) {
      setErro('Informe busca, grupo, subgrupo ou faixa de código de barras.');
      return [];
    }
    setBuscando(true);
    setErro('');
    try {
      const f = filtrosBusca();
      const { data } = await getAllProducts({
        ...f,
        indice: pagina,
        qtde: PAGE,
        ordenar: 'descricao',
        direcao: 'asc',
      });
      const lista = data?.items || [];
      setResultados(lista);
      setTotalBusca(data?.totalItems || lista.length);
      setPaginaBusca(data?.page || pagina);
      setMarcados({});
      return lista;
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível buscar os produtos.'));
      return [];
    } finally {
      setBuscando(false);
    }
  };

  const adicionarFiltrados = async () => {
    if (!temFiltro()) {
      setErro('Informe busca, grupo, subgrupo ou faixa de código de barras.');
      return;
    }
    setBuscando(true);
    setErro('');
    setAviso('');
    try {
      const f = filtrosBusca();
      const acumulado = [];
      let total = 0;
      let pagina = 1;
      while (acumulado.length < MAX_LISTA) {
        const { data } = await getAllProducts({
          ...f,
          indice: pagina,
          qtde: LOTE,
          ordenar: 'descricao',
          direcao: 'asc',
        });
        const lista = data?.items || [];
        total = data?.totalItems || lista.length;
        acumulado.push(...lista);
        if (!lista.length || acumulado.length >= total) break;
        pagina += 1;
      }
      const cortados = acumulado.slice(0, MAX_LISTA);
      incorporar(cortados);
      setPaginaCesta(1);
      setRemoverIds({});
      if (!total) setAviso('Nenhum produto nesse filtro.');
      else if (total > MAX_LISTA) {
        setAviso(`O filtro tem ${total} produtos. Entraram os primeiros ${MAX_LISTA}. Remova da lista o que não entra.`);
      } else {
        setAviso(`${cortados.length} produto(s) adicionados à lista.`);
      }
      await buscar(1);
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível adicionar os produtos do filtro.'));
    } finally {
      setBuscando(false);
    }
  };

  const removerSelecionados = () => {
    const ids = new Set(Object.entries(removerIds).filter(([, on]) => on).map(([id]) => Number(id)));
    if (!ids.size) return;
    setCesta((lista) => {
      const proxima = lista.filter((p) => !ids.has(p.id));
      const paginas = Math.max(1, Math.ceil(proxima.length / PAGE));
      setPaginaCesta((atual) => Math.min(atual, paginas));
      return proxima;
    });
    setRemoverIds({});
  };

  const setCampo = (campo, valor) => {
    setCadastro((prev) => {
      const next = { ...prev, [campo]: valor };
      if (campo === 'alterarCategoria' && valor) next.alterarSubCategoria = true;
      if (campo === 'alterarCategoria' && !valor) {
        next.alterarSubCategoria = false;
        next.categoriaId = '';
        next.subCategoriaId = '';
      }
      if (campo === 'categoriaId') next.subCategoriaId = '';
      return next;
    });
    setResultado(null);
  };

  const mexeCadastro = () => (
    cadastro.alterarCategoria || cadastro.alterarSubCategoria || cadastro.alterarMarca
    || cadastro.alterarFornecedor || cadastro.alterarUnidade || cadastro.alterarGrupoTributario
    || cadastro.alterarNcm || cadastro.alterarCest || cadastro.alterarQtdAtacado
    || cadastro.alterarValidade || cadastro.alterarComissao || cadastro.alterarAtivo
    || cadastro.alterarBalanca || cadastro.alterarCombustivel
    || cadastro.alterarObrigaIdentificacaoCliente
  );

  const mexePreco = () => operacao !== 'nenhuma' || arredondamento !== 'nenhum';

  const montarBody = () => {
    const tiposLista = [];
    if (tipos.varejo) tiposLista.push(0);
    if (tipos.atacado) tiposLista.push(1);
    if (tipos.promocional) tiposLista.push(2);
    if (tipos.custo) tiposLista.push(3);
    return {
      produtoIds: cesta.map((p) => p.id),
      estabelecimentoId: filialId ? Number(filialId) : 0,
      tabelaPrecoId: tabelaId ? Number(tabelaId) : 0,
      cadastro: {
        alterarCategoria: cadastro.alterarCategoria,
        categoriaId: cadastro.categoriaId ? Number(cadastro.categoriaId) : null,
        alterarSubCategoria: cadastro.alterarSubCategoria,
        subCategoriaId: cadastro.subCategoriaId ? Number(cadastro.subCategoriaId) : null,
        alterarMarca: cadastro.alterarMarca,
        marcaId: cadastro.marcaId ? Number(cadastro.marcaId) : null,
        alterarFornecedor: cadastro.alterarFornecedor,
        fornecedorId: cadastro.fornecedorId ? Number(cadastro.fornecedorId) : null,
        alterarUnidade: cadastro.alterarUnidade,
        unidade: cadastro.unidade,
        alterarGrupoTributario: cadastro.alterarGrupoTributario,
        grupoTributarioId: cadastro.grupoTributarioId ? Number(cadastro.grupoTributarioId) : null,
        alterarNcm: cadastro.alterarNcm,
        ncm: cadastro.ncm,
        alterarCest: cadastro.alterarCest,
        cest: cadastro.cest,
        alterarQtdAtacado: cadastro.alterarQtdAtacado,
        qtdAtacado: cadastro.qtdAtacado === '' ? null : num(cadastro.qtdAtacado),
        alterarValidade: cadastro.alterarValidade,
        validade: cadastro.validade === '' ? null : num(cadastro.validade),
        alterarComissao: cadastro.alterarComissao,
        percentualComissao: cadastro.percentualComissao === '' ? null : num(cadastro.percentualComissao),
        alterarAtivo: cadastro.alterarAtivo,
        ativo: cadastro.ativo,
        alterarBalanca: cadastro.alterarBalanca,
        balanca: Number(cadastro.balanca) || 0,
        alterarCombustivel: cadastro.alterarCombustivel,
        combustivel: cadastro.combustivel,
        alterarObrigaIdentificacaoCliente: cadastro.alterarObrigaIdentificacaoCliente,
        obrigaIdentificacaoCliente: cadastro.obrigaIdentificacaoCliente,
      },
      preco: {
        tipos: tiposLista,
        operacao,
        valor: num(valorPreco) ?? 0,
        arredondamento,
      },
    };
  };

  const validarAlteracoes = () => {
    if (!cesta.length) return 'Monte a lista de produtos no passo anterior.';
    if (!mexeCadastro() && !mexePreco()) return 'Marque ao menos uma alteração de cadastro ou de preço.';
    if (mexePreco() && !filialId) return 'Selecione a filial dos preços.';
    if (mexePreco() && !tabelaId) return 'Selecione a tabela de preço.';
    if (mexePreco() && !tipos.varejo && !tipos.atacado && !tipos.promocional && !tipos.custo) {
      return 'Escolha ao menos um preço (varejo, atacado, promocional ou custo).';
    }
    if (cadastro.alterarCategoria && !cadastro.categoriaId) return 'Selecione o novo grupo.';
    if (cadastro.alterarSubCategoria && !cadastro.subCategoriaId) return 'Selecione o novo subgrupo.';
    return '';
  };

  const verConferencia = async () => {
    const msg = validarAlteracoes();
    if (msg) {
      setErro(msg);
      return;
    }
    setErro('');
    setOk('');
    setBuscando(true);
    try {
      const { data } = await previewReajusteProdutos(montarBody());
      setResultado(data);
      setPasso(2);
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível montar a conferência.'));
    } finally {
      setBuscando(false);
    }
  };

  const aplicar = async () => {
    if (!resultado || (resultado.produtosCadastro === 0 && resultado.precosAlterados === 0)) return;
    const filial = filiais.find((f) => String(f.id) === String(filialId));
    const nome = filial?.nomeFantasia || filial?.razaoSocial || 'a filial';
    const extra = resultado.precoPorFilial
      ? ''
      : ' O preço é global e será replicado nas outras filiais.';
    if (!window.confirm(`Aplicar o reajuste em ${cesta.length} produto(s) de "${nome}"?${extra}`)) return;
    setBuscando(true);
    setErro('');
    try {
      const { data } = await aplicarReajusteProdutos(montarBody());
      setResultado(data);
      setOk(
        `Concluído: ${data.produtosCadastro || 0} produto(s) com cadastro alterado e ${data.precosAlterados || 0} preço(s) reajustado(s).`,
      );
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível aplicar o reajuste.'));
    } finally {
      setBuscando(false);
    }
  };

  const visiveisMarcados = resultados.filter((p) => marcados[p.id] && !idsNaCesta.has(p.id));
  const paginasBusca = Math.max(1, Math.ceil(totalBusca / PAGE));
  const paginasCesta = Math.max(1, Math.ceil(cesta.length / PAGE));
  const paginaCestaAtual = Math.min(paginaCesta, paginasCesta);
  const cestaPagina = cesta.slice((paginaCestaAtual - 1) * PAGE, paginaCestaAtual * PAGE);
  const idsRemoverPagina = cestaPagina.filter((p) => removerIds[p.id]);
  const todosRemoverPagina = cestaPagina.length > 0 && idsRemoverPagina.length === cestaPagina.length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Reajuste de produtos"
        subtitle="Monte a lista como nas etiquetas e no planejamento, depois escolha o que alterar no cadastro e nos preços."
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}
      {aviso && passo === 0 && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setAviso('')}>{aviso}</Alert>}

      <Stepper activeStep={passo} sx={{ mb: 3 }}>
        {PASSOS.map((rotulo) => (
          <Step key={rotulo}><StepLabel>{rotulo}</StepLabel></Step>
        ))}
      </Stepper>

      {passo === 0 && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <BuscaProdutoCampo
                value={termo}
                onChange={setTermo}
                onSearch={() => buscar(1)}
                fullWidth
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  select
                  size="small"
                  label="Grupo"
                  value={categoriaId}
                  onChange={(e) => { setCategoriaId(e.target.value); setSubCategoriaId(''); }}
                  fullWidth
                >
                  <MenuItem value="">Todos</MenuItem>
                  {categorias.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{nomeDe(c)}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Subgrupo"
                  value={subCategoriaId}
                  onChange={(e) => setSubCategoriaId(e.target.value)}
                  disabled={!categoriaId}
                  fullWidth
                >
                  <MenuItem value="">Todos</MenuItem>
                  {subsFiltro.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>{nomeDe(s)}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Código de barras de"
                  value={eanDe}
                  onChange={(e) => setEanDe(e.target.value)}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Código de barras até"
                  value={eanAte}
                  onChange={(e) => setEanAte(e.target.value)}
                  fullWidth
                />
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="contained" onClick={() => buscar(1)} disabled={buscando}>
                  Buscar
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => incorporar(visiveisMarcados)}
                  disabled={!visiveisMarcados.length}
                >
                  Adicionar selecionados
                </Button>
                <Button variant="outlined" onClick={adicionarFiltrados} disabled={buscando}>
                  Adicionar filtrados
                </Button>
              </Stack>
              {totalBusca > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {totalBusca} produto(s) no filtro. A tabela abaixo é uma página; use Adicionar filtrados para trazer o grupo inteiro e remova o que não entra.
                </Typography>
              )}
            </Stack>
          </Paper>

          <ResponsiveTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Código</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>EAN</TableCell>
                  <TableCell>Grupo</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {resultados.map((p) => {
                  const ja = idsNaCesta.has(p.id);
                  return (
                    <TableRow key={p.id} hover selected={ja}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={ja || Boolean(marcados[p.id])}
                          disabled={ja}
                          onChange={(e) => setMarcados((m) => ({ ...m, [p.id]: e.target.checked }))}
                        />
                      </TableCell>
                      <TableCell>{p.codigo}</TableCell>
                      <TableCell>{p.descricao}</TableCell>
                      <TableCell>{p.ean || '—'}</TableCell>
                      <TableCell>{p.categoriaNome || p.categoria || '—'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={ja ? 'Já está na lista' : 'Adicionar'}>
                          <span>
                            <IconButton size="small" disabled={ja} onClick={() => incorporar([p])} aria-label="Adicionar">
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!resultados.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        Busque e adicione os produtos que entrarão no reajuste.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ResponsiveTable>
          {paginasBusca > 1 && (
            <Stack direction="row" justifyContent="flex-end">
              <Pagination
                count={paginasBusca}
                page={paginaBusca}
                onChange={(_, v) => buscar(v)}
                color="primary"
                disabled={buscando}
              />
            </Stack>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
              <Typography fontWeight={700}>
                Lista do reajuste ({cesta.length})
              </Typography>
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                disabled={!Object.values(removerIds).some(Boolean)}
                onClick={removerSelecionados}
              >
                Remover selecionados
              </Button>
            </Stack>
            <ResponsiveTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={todosRemoverPagina}
                        indeterminate={idsRemoverPagina.length > 0 && !todosRemoverPagina}
                        disabled={!cestaPagina.length}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setRemoverIds((atual) => {
                            const proximo = { ...atual };
                            cestaPagina.forEach((p) => { proximo[p.id] = on; });
                            return proximo;
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>EAN</TableCell>
                    <TableCell>Grupo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cestaPagina.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={Boolean(removerIds[p.id])}
                          onChange={(e) => setRemoverIds((m) => ({ ...m, [p.id]: e.target.checked }))}
                        />
                      </TableCell>
                      <TableCell>{p.codigo}</TableCell>
                      <TableCell>{p.descricao}</TableCell>
                      <TableCell>{p.ean || '—'}</TableCell>
                      <TableCell>{p.categoria || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {!cesta.length && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          Nenhum produto na lista ainda.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ResponsiveTable>
            {paginasCesta > 1 && (
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                <Pagination
                  count={paginasCesta}
                  page={paginaCestaAtual}
                  onChange={(_, v) => setPaginaCesta(v)}
                  color="primary"
                />
              </Stack>
            )}
          </Paper>

          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              disabled={!cesta.length}
              onClick={() => { setErro(''); setPasso(1); }}
            >
              Continuar
            </Button>
          </Stack>
        </Stack>
      )}

      {passo === 1 && (
        <Stack spacing={2}>
          <Alert severity="info">
            {cesta.length} produto(s) na lista. Marque só o que deve mudar; o restante permanece como está.
          </Alert>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight={700} sx={{ mb: 1.5 }}>Filial e tabela</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                size="small"
                label="Filial dos preços"
                value={filialId}
                onChange={(e) => { setFilialId(e.target.value); setResultado(null); }}
                fullWidth
              >
                {filiais.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.nomeFantasia || f.razaoSocial || `Filial ${f.id}`}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Tabela de preço"
                value={tabelaId}
                onChange={(e) => { setTabelaId(e.target.value); setResultado(null); }}
                fullWidth
              >
                {tabelasFilial.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>{t.nome}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight={700} sx={{ mb: 0.5 }}>Cadastro</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cada linha só grava se o checkbox estiver marcado.
            </Typography>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Classificação</Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <CampoLinha
                marcado={cadastro.alterarCategoria}
                onMarcado={(v) => setCampo('alterarCategoria', v)}
                label="Grupo"
              >
                <TextField select size="small" label="Novo grupo" value={cadastro.categoriaId} onChange={(e) => setCampo('categoriaId', e.target.value)} disabled={!cadastro.alterarCategoria} fullWidth>
                  {categorias.map((c) => <MenuItem key={c.id} value={String(c.id)}>{nomeDe(c)}</MenuItem>)}
                </TextField>
              </CampoLinha>
              <CampoLinha
                marcado={cadastro.alterarSubCategoria}
                onMarcado={(v) => setCampo('alterarSubCategoria', v)}
                label="Subgrupo"
              >
                <TextField
                  select
                  size="small"
                  label="Novo subgrupo"
                  value={cadastro.subCategoriaId}
                  onChange={(e) => setCampo('subCategoriaId', e.target.value)}
                  disabled={!cadastro.alterarSubCategoria || !cadastro.categoriaId}
                  helperText={!cadastro.categoriaId ? 'Marque o grupo antes.' : ' '}
                  fullWidth
                >
                  {subsCadastro.map((s) => <MenuItem key={s.id} value={String(s.id)}>{nomeDe(s)}</MenuItem>)}
                </TextField>
              </CampoLinha>
              <CampoLinha marcado={cadastro.alterarMarca} onMarcado={(v) => setCampo('alterarMarca', v)} label="Marca">
                <TextField select size="small" label="Nova marca" value={cadastro.marcaId} onChange={(e) => setCampo('marcaId', e.target.value)} disabled={!cadastro.alterarMarca} fullWidth>
                  <MenuItem value="">Limpar</MenuItem>
                  {marcas.map((m) => <MenuItem key={m.id} value={String(m.id)}>{nomeDe(m)}</MenuItem>)}
                </TextField>
              </CampoLinha>
              <CampoLinha marcado={cadastro.alterarFornecedor} onMarcado={(v) => setCampo('alterarFornecedor', v)} label="Fornecedor">
                <TextField select size="small" label="Novo fornecedor" value={cadastro.fornecedorId} onChange={(e) => setCampo('fornecedorId', e.target.value)} disabled={!cadastro.alterarFornecedor} fullWidth>
                  <MenuItem value="">Limpar</MenuItem>
                  {fornecedores.map((f) => <MenuItem key={f.id} value={String(f.id)}>{nomeDe(f)}</MenuItem>)}
                </TextField>
              </CampoLinha>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Comercial</Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <CampoLinha marcado={cadastro.alterarUnidade} onMarcado={(v) => setCampo('alterarUnidade', v)} label="Unidade">
                <TextField select size="small" label="Unidade" value={cadastro.unidade} onChange={(e) => setCampo('unidade', e.target.value)} disabled={!cadastro.alterarUnidade} fullWidth>
                  {UNIDADES.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </TextField>
              </CampoLinha>
              <CampoLinha marcado={cadastro.alterarQtdAtacado} onMarcado={(v) => setCampo('alterarQtdAtacado', v)} label="Qtd. atacado">
                <TextField size="small" label="Quantidade" value={cadastro.qtdAtacado} onChange={(e) => setCampo('qtdAtacado', e.target.value)} disabled={!cadastro.alterarQtdAtacado} fullWidth />
              </CampoLinha>
              <CampoLinha marcado={cadastro.alterarComissao} onMarcado={(v) => setCampo('alterarComissao', v)} label="Comissão %">
                <TextField size="small" label="Percentual" value={cadastro.percentualComissao} onChange={(e) => setCampo('percentualComissao', e.target.value)} disabled={!cadastro.alterarComissao} fullWidth />
              </CampoLinha>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Flags</Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <CampoLinha marcado={cadastro.alterarAtivo} onMarcado={(v) => setCampo('alterarAtivo', v)} label="Ativo">
                <TextField select size="small" label="Situação" value={cadastro.ativo ? '1' : '0'} onChange={(e) => setCampo('ativo', e.target.value === '1')} disabled={!cadastro.alterarAtivo} fullWidth>
                  <MenuItem value="1">Ativo</MenuItem>
                  <MenuItem value="0">Inativo</MenuItem>
                </TextField>
              </CampoLinha>
              <CampoLinha marcado={cadastro.alterarBalanca} onMarcado={(v) => setCampo('alterarBalanca', v)} label="Balança">
                <TextField
                  select
                  size="small"
                  label="Modo balança"
                  value={String(cadastro.balanca)}
                  onChange={(e) => setCampo('balanca', Number(e.target.value))}
                  disabled={!cadastro.alterarBalanca}
                  fullWidth
                >
                  {[0, 1, 2, 3].map((v) => (
                    <MenuItem key={v} value={String(v)}>{NOME_BALANCA[v]}</MenuItem>
                  ))}
                </TextField>
              </CampoLinha>
              <CampoLinha marcado={cadastro.alterarCombustivel} onMarcado={(v) => setCampo('alterarCombustivel', v)} label="Combustível">
                <TextField
                  select
                  size="small"
                  label="Produto combustível"
                  value={cadastro.combustivel ? '1' : '0'}
                  onChange={(e) => setCampo('combustivel', e.target.value === '1')}
                  disabled={!cadastro.alterarCombustivel}
                  fullWidth
                >
                  <MenuItem value="1">Sim</MenuItem>
                  <MenuItem value="0">Não</MenuItem>
                </TextField>
              </CampoLinha>
              <CampoLinha
                marcado={cadastro.alterarObrigaIdentificacaoCliente}
                onMarcado={(v) => setCampo('alterarObrigaIdentificacaoCliente', v)}
                label="Identificação"
              >
                <TextField
                  select
                  size="small"
                  label="Obriga CPF/cadastro na venda"
                  value={cadastro.obrigaIdentificacaoCliente ? '1' : '0'}
                  onChange={(e) => setCampo('obrigaIdentificacaoCliente', e.target.value === '1')}
                  disabled={!cadastro.alterarObrigaIdentificacaoCliente}
                  fullWidth
                >
                  <MenuItem value="1">Sim</MenuItem>
                  <MenuItem value="0">Não</MenuItem>
                </TextField>
              </CampoLinha>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Fiscal</Typography>
            <Grid container spacing={1.5}>
              <CampoLinha marcado={cadastro.alterarGrupoTributario} onMarcado={(v) => setCampo('alterarGrupoTributario', v)} label="Grupo tributário">
                <TextField select size="small" label="Grupo tributário" value={cadastro.grupoTributarioId} onChange={(e) => setCampo('grupoTributarioId', e.target.value)} disabled={!cadastro.alterarGrupoTributario} fullWidth>
                  {gruposTrib.map((g) => <MenuItem key={g.id} value={String(g.id)}>{nomeDe(g)}</MenuItem>)}
                </TextField>
              </CampoLinha>
              <CampoLinha marcado={cadastro.alterarNcm} onMarcado={(v) => setCampo('alterarNcm', v)} label="NCM">
                <TextField size="small" label="NCM" value={cadastro.ncm} onChange={(e) => setCampo('ncm', e.target.value)} disabled={!cadastro.alterarNcm} fullWidth />
              </CampoLinha>
              <CampoLinha marcado={cadastro.alterarCest} onMarcado={(v) => setCampo('alterarCest', v)} label="CEST">
                <TextField size="small" label="CEST" value={cadastro.cest} onChange={(e) => setCampo('cest', e.target.value)} disabled={!cadastro.alterarCest} fullWidth />
              </CampoLinha>
              <CampoLinha marcado={cadastro.alterarValidade} onMarcado={(v) => setCampo('alterarValidade', v)} label="Validade (dias)">
                <TextField size="small" label="Dias" value={cadastro.validade} onChange={(e) => setCampo('validade', e.target.value)} disabled={!cadastro.alterarValidade} fullWidth />
              </CampoLinha>
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight={700} sx={{ mb: 0.5 }}>Preços</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Escolha quais preços entram e como recalcular. Deixe a operação em “somente arredondar” se quiser só o final ,00 ou ,99.
            </Typography>
            <FormGroup row sx={{ mb: 2 }}>
              {[
                ['varejo', 'Varejo'],
                ['atacado', 'Atacado'],
                ['promocional', 'Promocional'],
                ['custo', 'Custo'],
              ].map(([chave, rotulo]) => (
                <FormControlLabel
                  key={chave}
                  control={(
                    <Checkbox
                      checked={tipos[chave]}
                      onChange={(e) => {
                        setTipos((prev) => ({ ...prev, [chave]: e.target.checked }));
                        setResultado(null);
                      }}
                    />
                  )}
                  label={rotulo}
                />
              ))}
            </FormGroup>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                size="small"
                label="Operação"
                value={operacao}
                onChange={(e) => { setOperacao(e.target.value); setResultado(null); }}
                fullWidth
              >
                <MenuItem value="percentual">Aumentar em %</MenuItem>
                <MenuItem value="valor">Incrementar valor</MenuItem>
                <MenuItem value="nenhuma">Somente arredondar</MenuItem>
              </TextField>
              {operacao !== 'nenhuma' && (
                <TextField
                  size="small"
                  label={operacao === 'percentual' ? 'Percentual' : 'Valor'}
                  value={valorPreco}
                  onChange={(e) => { setValorPreco(e.target.value); setResultado(null); }}
                  helperText="Negativo reduz. Ex.: 10 ou -2,50"
                  fullWidth
                />
              )}
              <TextField
                select
                size="small"
                label="Arredondamento"
                value={arredondamento}
                onChange={(e) => { setArredondamento(e.target.value); setResultado(null); }}
                fullWidth
              >
                <MenuItem value="nenhum">Não arredondar</MenuItem>
                <MenuItem value="final00">Final ,00</MenuItem>
                <MenuItem value="final99">Final ,99</MenuItem>
              </TextField>
            </Stack>
          </Paper>

          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button onClick={() => setPasso(0)} disabled={buscando}>Voltar</Button>
            <Button variant="contained" disabled={buscando} onClick={verConferencia}>
              {buscando ? 'Conferindo…' : 'Conferir'}
            </Button>
          </Stack>
          {buscando && <LinearProgress />}
        </Stack>
      )}

      {passo === 2 && resultado && (
        <Stack spacing={2}>
          <Alert severity="info">
            {resultado.totalProdutos} produto(s) na lista.
            {' '}{resultado.produtosCadastro} terão cadastro alterado e {resultado.precosAlterados} preço(s) serão reajustados.
            {!resultado.precoPorFilial && resultado.precosAlterados > 0
              ? ' Preço global: a alteração replica para as outras filiais.'
              : ''}
          </Alert>
          {resultado.amostra?.length > 0 && (
            <Paper variant="outlined">
              <ResponsiveTable>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell>Campo</TableCell>
                      <TableCell>De</TableCell>
                      <TableCell>Para</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resultado.amostra.map((linha, indice) => (
                      <TableRow key={`${linha.codigo}-${linha.campo}-${indice}`}>
                        <TableCell>{linha.codigo}</TableCell>
                        <TableCell>{linha.descricao}</TableCell>
                        <TableCell>{linha.campo}</TableCell>
                        <TableCell>{linha.de}</TableCell>
                        <TableCell>{linha.para}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTable>
            </Paper>
          )}
          {resultado.amostra?.length >= 40 && (
            <Typography variant="body2" color="text.secondary">
              A tabela mostra as primeiras alterações. O total acima inclui o restante.
            </Typography>
          )}
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button onClick={() => setPasso(1)} disabled={buscando}>Voltar</Button>
            <Button
              variant="contained"
              disabled={buscando || (resultado.produtosCadastro === 0 && resultado.precosAlterados === 0)}
              onClick={aplicar}
            >
              {buscando ? 'Aplicando…' : 'Aplicar reajuste'}
            </Button>
          </Stack>
          {buscando && <LinearProgress />}
        </Stack>
      )}
    </Box>
  );
}

function CampoLinha({ marcado, onMarcado, label, children }) {
  return (
    <Grid item xs={12} md={6}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <FormControlLabel
          sx={{ mt: 0.5, minWidth: 130, mr: 0 }}
          control={<Checkbox checked={marcado} onChange={(e) => onMarcado(e.target.checked)} />}
          label={label}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      </Stack>
    </Grid>
  );
}
